import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { PlusIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { GanttChart } from '@/features/roadmaps/components/gantt-chart'
import { RoadmapActivityDialog } from '@/features/roadmaps/components/roadmap-activity-dialog'
import { RoadmapLegend } from '@/features/roadmaps/components/roadmap-legend'
import { RoadmapPercentageGuide } from '@/features/roadmaps/components/roadmap-percentage-guide'
import {
	ROADMAP_2026_OVERLAYS,
	ROADMAP_2026_WINDOW,
	ROADMAP_CATEGORIES
} from '@/features/roadmaps/constants'
import { useGetRoadmapActivities } from '@/features/roadmaps/queries/use-get-roadmap-activities'
import { useGetRoadmapPermissions } from '@/features/roadmaps/queries/use-get-roadmap-permissions'
import type { RoadmapActivity, RoadmapConfig } from '@/features/roadmaps/types'
import { useTranslation } from '@/translations/i18n'

/**
 * The 2026 roadmap. Activities come from the API for the window below; the rows
 * and the overlay bands are client-side, so a category with no activities still
 * gets a row instead of disappearing.
 */
function Roadmap2026() {
	const { t } = useTranslation()
	const [isDialogOpen, setIsDialogOpen] = useState(false)
	// The activity being edited; null while creating.
	const [editingActivity, setEditingActivity] =
		useState<RoadmapActivity | null>(null)

	const {
		data: activities,
		isPending,
		isError
	} = useGetRoadmapActivities(ROADMAP_2026_WINDOW)

	// Whether to show the write UI. Defaults to hidden until the check resolves,
	// so the button never flashes for someone who cannot use it. The backend is
	// the real gate; this only decides what to render.
	const { data: permissions } = useGetRoadmapPermissions()
	const canEdit = permissions?.canEdit ?? false

	const config = useMemo<RoadmapConfig>(
		() => ({
			...ROADMAP_2026_WINDOW,
			overlays: ROADMAP_2026_OVERLAYS,
			categories: ROADMAP_CATEGORIES.map((category) => ({
				...category,
				activities: (activities ?? []).filter(
					(activity) => activity.category === category.id
				)
			}))
		}),
		[activities]
	)

	const openCreate = () => {
		setEditingActivity(null)
		setIsDialogOpen(true)
	}

	const openEdit = (activity: RoadmapActivity) => {
		setEditingActivity(activity)
		setIsDialogOpen(true)
	}

	// Nothing but the spinner until the roadmap is in: no header, no legend, no
	// empty chart to flash before the data lands.
	if (isPending) {
		return (
			<div className='flex h-full items-center justify-center'>
				<Spinner size={32} />
			</div>
		)
	}

	return (
		<div className='flex flex-col gap-6 l:px-10 px-4 py-6'>
			<title>{t('roadmaps:page_title_2026')}</title>

			<div className='flex items-center justify-between gap-4'>
				<h1 className='font-medium text-lg text-neutral-900'>
					{t('roadmaps:page_title_2026')}
				</h1>

				{canEdit ? (
					<Button
						onClick={openCreate}
						type='button'
						variant='primary'
					>
						<PlusIcon />
						{t('roadmaps:create_activity')}
					</Button>
				) : null}
			</div>

			<RoadmapLegend />

			{isError ? (
				<p className='text-danger-500 text-sm'>
					{t('roadmaps:load_error')}
				</p>
			) : (
				<GanttChart
					config={config}
					onActivitySelect={canEdit ? openEdit : undefined}
				/>
			)}

			<RoadmapPercentageGuide />

			{canEdit ? (
				<RoadmapActivityDialog
					activity={editingActivity}
					onOpenChange={setIsDialogOpen}
					open={isDialogOpen}
				/>
			) : null}
		</div>
	)
}

export { Roadmap2026 }
